import { Badge } from "@/components/ui/baseUi/badge"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { word } from "@/modules/core/utils/i18n"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import type { ScoreboardRankingEntity } from "@/modules/scores/scoreboard/entities/scoreboardRankingEntity"
import { getDisplayedScore } from "@/modules/scores/scoreUtils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { shortenText } from "@/modules/utils/stringUtils"
import { CommentIcon } from "@images/svgIcons/commentIcon"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useScoreboardSidebar } from "@modules/scores/scoreboard/hooks/useScoreboardSidebar"
import { getScoreDifficultyWording, ScoreDifficultyEnum, ScoreType } from "@modules/scores/scoreTypes"
import { useUnit } from "@modules/utils/hooks/useUnit"
import { StarIcon } from "lucide-react"

type Props = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity
  selectedExercise: SessionBlockExerciseEntity
  userScore: ScoreboardRankingEntity
  myRanking: ScoreboardRankingEntity | null
  className?: string
} & React.ComponentProps<"div">

export function ScoreboardDetailsCard({ session, selectedExercise, userScore, myRanking, className, ...props }: Props) {
  const isMyRanking = userScore.userId === myRanking?.userId ? true : false
  const { navigateToScoreboardIndivView } = useScoreboardSidebar()
  const unit = useUnit()
  const language = useLanguage()

  return (
    <div
      className={cn(
        "mb-2.5 flex h-[86px] w-full cursor-pointer items-center justify-between rounded-[20px] p-3 px-5",
        isMyRanking ? "bg-linear-to-r from-[#E4F56C] to-[#CEE743]" : "bg-neutral-50",
        className,
      )}
      onClick={() => navigateToScoreboardIndivView(session, selectedExercise, userScore.userId)}
      {...props}
    >
      <div className="flex w-9/10 items-center justify-start">
        <div className="relative mr-2 mb-1">
          <ProfileAvatar
            profile={{ avatar: userScore.avatar ?? null, username: userScore.firstname || "" }}
            className="size-13.5"
          />
          {userScore.scoreType && ![ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(userScore.scoreType) && (
            <div
              className={cn(
                "absolute bottom-[-12px] left-1/2 flex h-6.5 min-w-6.5 -translate-x-1/2 transform items-center justify-center rounded-[13px] px-2 py-1 ring-2",
                isMyRanking ? "bg-secondary-500 ring-[#DFF263]" : "bg-neutral-600 ring-neutral-50",
              )}
            >
              <Text fontStyle="LM" className="font-semibold text-textOnColor-body">
                {userScore.position}
              </Text>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-[3px] overflow-hidden p-1">
          <Text
            className={cn(
              "leading-[normal] font-semibold",
              isMyRanking ? "text-secondary-500" : "text-textDefault-heading",
            )}
            fontStyle="LL"
          >
            {shortenText(userScore.firstname + " " + userScore.lastname, 45)}
          </Text>

          {userScore.score && userScore.score.valueDecimal !== null && (
            <div className="flex flex-row items-center gap-2">
              {userScore.score && userScore.score.valueDecimal !== null && (
                <Text
                  className={cn("leading-[normal]", isMyRanking ? "text-secondary-500" : "text-textDefault-body")}
                  fontStyle="BS"
                >
                  {getDisplayedScore(
                    userScore.score.valueDecimal,
                    userScore.score.additionalValueDecimal,
                    userScore.scoreType,
                    userScore.score.timeCapExceeded ?? false,
                    unit,
                    language,
                  )}
                </Text>
              )}
              {userScore.scoreType &&
                ![ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(userScore.scoreType) &&
                userScore.score?.difficulty === ScoreDifficultyEnum.RX && (
                  <Badge
                    variant={isMyRanking ? "positiveSubtle" : "primaryIntense"}
                    size="md"
                    className={cn(
                      "pr-2.5 italic",
                      isMyRanking ? "ring-badge-positive-text-subtle bg-transparent ring-1" : "",
                    )}
                  >
                    {getScoreDifficultyWording(ScoreDifficultyEnum.RX)}
                  </Badge>
                )}
              {userScore.score?.personalRecord && (
                <Badge
                  variant={isMyRanking ? "positiveSubtle" : "primaryIntense"}
                  size="md"
                  className={cn(
                    "pr-2.5 italic",
                    isMyRanking ? "ring-badge-positive-text-subtle bg-transparent ring-1" : "",
                  )}
                >
                  <StarIcon className="currentColor" />
                  {word("scoreboard.personalRecord.shortLabel")}
                </Badge>
              )}
            </div>
          )}
          {userScore.scoreType &&
            [ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(userScore.scoreType) &&
            userScore.score?.note && (
              <Text
                className={cn(
                  "mr-2 overflow-hidden leading-[normal] overflow-ellipsis whitespace-nowrap",
                  isMyRanking ? "text-textOnSurface-primary-default" : "text-textOnSurface-subtle",
                )}
                fontStyle="BS"
              >
                {userScore.score.note}
              </Text>
            )}
        </div>
      </div>
      <div>
        {(userScore.score?.note || (userScore.score?.medias && userScore.score.medias.length > 0)) && (
          <CommentIcon
            className={cn(isMyRanking ? "text-secondary-500" : "text-textDefault-heading")}
            color="currentColor"
          />
        )}
      </div>
    </div>
  )
}
